import React, {createContext, useCallback, useMemo, useState} from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {APP_TUTORIAL_STORAGE_KEY, buildAppTutorialSteps} from './appTutorialSteps';
import {navigateWhenReady} from './navigationRef';

export const TourContext = createContext();

export const TourProvider = ({children}) => {
    // Legacy tooltip state kept for existing CustomTooltip integrations.
    const [currentStep, setCurrentStep] = useState(-1);
    const [tourCompleted, setTourCompleted] = useState(true);
    const [appTutorialActive, setAppTutorialActive] = useState(false);
    const [appTutorialSteps, setAppTutorialSteps] = useState([]);
    const [appTutorialStepIndex, setAppTutorialStepIndex] = useState(0);
    const [appTutorialSource, setAppTutorialSource] = useState('auto');

    const activeAppTutorialStep = appTutorialSteps[appTutorialStepIndex] || null;

    const navigateToStep = useCallback((step) => {
        if (!step?.routeName) {
            return;
        }

        navigateWhenReady(step.routeName, step.params);
    }, []);

    const markAppTutorialSeen = useCallback(async () => {
        try {
            await AsyncStorage.setItem(APP_TUTORIAL_STORAGE_KEY, 'true');
        } catch (_error) {
            // Tutorial state is UI-only; failing to persist must not block the app.
        }
    }, []);

    const startAppTutorial = useCallback((options = {}) => {
        const steps = buildAppTutorialSteps(options);

        if (!steps.length) {
            return;
        }

        setAppTutorialSteps(steps);
        setAppTutorialStepIndex(0);
        setAppTutorialSource(options.source || 'manual');
        setAppTutorialActive(true);
        setTourCompleted(false);
        setCurrentStep(-1);

        globalThis.setTimeout(() => navigateToStep(steps[0]), options.delay || 150);
    }, [navigateToStep]);

    const maybeStartAppTutorial = useCallback(async (options = {}) => {
        try {
            const seen = await AsyncStorage.getItem(APP_TUTORIAL_STORAGE_KEY);

            if (seen === 'true' && !options.force) {
                return false;
            }
        } catch (_error) {
            // Continue and show the tutorial if the seen flag cannot be read.
        }

        startAppTutorial({
            ...options,
            source: options.source || 'auto',
        });

        return true;
    }, [startAppTutorial]);

    const endAppTutorial = useCallback(async ({markSeen = true} = {}) => {
        setAppTutorialActive(false);
        setAppTutorialStepIndex(0);
        setTourCompleted(true);
        setCurrentStep(-1);

        if (markSeen) {
            await markAppTutorialSeen();
        }
    }, [markAppTutorialSeen]);

    const nextAppTutorialStep = useCallback(() => {
        const next = appTutorialStepIndex + 1;

        if (next >= appTutorialSteps.length) {
            endAppTutorial({markSeen: true});
            return;
        }

        setAppTutorialStepIndex(next);
        navigateToStep(appTutorialSteps[next]);
    }, [appTutorialStepIndex, appTutorialSteps, endAppTutorial, navigateToStep]);

    const previousAppTutorialStep = useCallback(() => {
        const next = Math.max(0, appTutorialStepIndex - 1);

        setAppTutorialStepIndex(next);
        navigateToStep(appTutorialSteps[next]);
    }, [appTutorialStepIndex, appTutorialSteps, navigateToStep]);

    const skipAppTutorialSection = useCallback(() => {
        const section = activeAppTutorialStep?.section;

        if (!section) {
            nextAppTutorialStep();
            return;
        }

        const nextIndex = appTutorialSteps.findIndex((step, index) => index > appTutorialStepIndex && step.section !== section);

        if (nextIndex === -1) {
            endAppTutorial({markSeen: true});
            return;
        }

        setAppTutorialStepIndex(nextIndex);
        navigateToStep(appTutorialSteps[nextIndex]);
    }, [activeAppTutorialStep?.section, appTutorialStepIndex, appTutorialSteps, endAppTutorial, navigateToStep, nextAppTutorialStep]);

    const nextStep = useCallback(() => {
        setCurrentStep((prev) => prev + 1);
    }, []);

    const resetTour = useCallback(() => {
        setCurrentStep(0);
        setTourCompleted(false);
    }, []);

    const startTour = useCallback((options = {}) => {
        startAppTutorial({
            ...options,
            force: true,
            source: options.source || 'manual',
        });
    }, [startAppTutorial]);

    const endTour = useCallback(() => {
        setTourCompleted(true);
        setCurrentStep(-1);
        endAppTutorial({markSeen: true});
    }, [endAppTutorial]);

    const value = useMemo(() => ({
        currentStep,
        nextStep,
        tourCompleted,
        setTourCompleted,
        resetTour,
        endTour,
        startTour,
        appTutorialActive,
        appTutorialSteps,
        appTutorialStepIndex,
        activeAppTutorialStep,
        appTutorialSource,
        startAppTutorial,
        maybeStartAppTutorial,
        nextAppTutorialStep,
        previousAppTutorialStep,
        skipAppTutorialSection,
        endAppTutorial,
    }), [
        activeAppTutorialStep,
        appTutorialActive,
        appTutorialSource,
        appTutorialStepIndex,
        appTutorialSteps,
        currentStep,
        endAppTutorial,
        endTour,
        maybeStartAppTutorial,
        nextAppTutorialStep,
        nextStep,
        previousAppTutorialStep,
        resetTour,
        skipAppTutorialSection,
        startAppTutorial,
        startTour,
        tourCompleted,
    ]);

    return (
        <TourContext.Provider value={value}>
            {children}
        </TourContext.Provider>
    );
};
